// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: src/pages/POSPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 3.7 – FIX: Remoção de Variável Não Utilizada (ESLint)
 * Impacto da Versão: [src/pages/POSPage.js]
 * Data: 18/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [FIX] Removida declaração `const installmentValue` que não estava sendo usada, corrigindo warning do compilador.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db, auth } from '../firebase/config'; 
import { 
  collection, 
  getDocs, 
  doc, 
  runTransaction, 
  serverTimestamp, 
  query, 
  where, 
  increment, 
  addDoc, 
  updateDoc, 
  orderBy, 
  limit, 
  writeBatch 
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  Loader2, 
  ShoppingCart, 
  Lock, 
  Unlock, 
  DollarSign, 
  MinusCircle, 
  PlusCircle, 
  Package, 
  Printer,
  History,
  Calculator,
  CreditCard, // Ícone Cartão
  Smartphone, // Ícone Pix
  CheckCircle, // Ícone Sucesso
  Wifi, // Ícone Conexão
  AlertTriangle // Ícone Alerta
} from 'lucide-react';

import CustomerSearch from '../components/pos/CustomerSearch';
import ProductGrid from '../components/pos/ProductGrid';
import CartPane from '../components/pos/CartPane';
import PointsCard from '../components/pos/PointsCard';
import CashMovementModalContent from '../components/pos/CashMovementModalContent';
import productService from '../services/productService';

// [GS-NEW] Imports Financeiros
import financialService from '../services/financialService';
import { FINANCIAL_TYPES, FINANCIAL_STATUS } from '../utils/constants';

const POINT_RATES = { primeRate: 0.05, secondaryRate: 0.02 };
const WALK_IN_CUSTOMER = { id: 'consumidor-final', name: 'Consumidor Final' };

const POSPage = ({ openModal, closeModal }) => {
  const [products, setProducts] = useState([]);
  const [sortOrder, setSortOrder] = useState('default');
  const [allCustomers, setAllCustomers] = useState([]);
  const [planRules, setPlanRules] = useState({});
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [discountInfo, setDiscountInfo] = useState({ discount: 0, cost: 0 }); 
  const [manualDiscountPercentage, setManualDiscountPercentage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cashierSession, setCashierSession] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const amountInputRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setLoggedInUser({ uid: user.uid, name: user.displayName || user.email });
      else setLoggedInUser(null);
    });
    return () => unsubscribe();
  }, []);

  const fetchEssentialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [productsData, customersSnapshot, plansSnapshot] = await Promise.all([
        productService.getProductsForPOS(),
        getDocs(collection(db, "users")),
        getDocs(collection(db, "club_plans"))
      ]);
      setProducts(productsData);
      setAllCustomers(customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      const rules = {};
      if (plansSnapshot) plansSnapshot.forEach(doc => { 
          const data = doc.data(); 
          rules[data.name] = { discountLimitPercentage: typeof data.discountPercentage === 'number' ? data.discountPercentage / 100 : 0, primeRate: POINT_RATES.primeRate, secondaryRate: POINT_RATES.secondaryRate }; 
      });
      rules['Aspirante'] = { discountLimitPercentage: 0.05, primeRate: POINT_RATES.primeRate, secondaryRate: POINT_RATES.secondaryRate };
      setPlanRules(rules);
    } catch (error) {
      console.error("Erro ao carregar dados essenciais para o PDV:", error);
      openModal(<p>Não foi possível carregar os dados para venda. Verifique sua conexão.</p>, "Erro de Carregamento");
    } finally { setIsLoading(false); }
  }, [openModal]);

  const checkOpenSession = useCallback(async () => {
    if (!loggedInUser) { setIsSessionLoading(true); return; }
    setIsSessionLoading(true);
    try {
      const q = query(collection(db, "cashier_sessions"), where("status", "==", "open"), where("openedBy.uid", "==", loggedInUser.uid));
      const snap = await getDocs(q);
      if (!snap.empty) { const sessionDoc = snap.docs[0]; setCashierSession({ id: sessionDoc.id, ...sessionDoc.data() }); await fetchEssentialData(); } 
      else { setCashierSession(null); }
    } catch (error) {
      console.error("Erro ao verificar sessão de caixa:", error);
      openModal(<p>Erro ao verificar o status do caixa.</p>, "Erro de Sessão");
    } finally { setIsSessionLoading(false); }
  }, [fetchEssentialData, openModal, loggedInUser]);

  useEffect(() => { if (loggedInUser) checkOpenSession(); }, [checkOpenSession, loggedInUser]);

  const getEffectivePrice = useCallback((p) => {
    const decided = Number(p?.decidedPrice) || 0;
    const base = Number(p?.price) || 0;
    return decided > 0 ? decided : base;
  }, []);

  const sortedProducts = useMemo(() => {
    if (sortOrder === 'default') return products;
    let list = [...products];
    if (sortOrder === 'price-desc') list.sort((a, b) => getEffectivePrice(b) - getEffectivePrice(a));
    if (sortOrder === 'price-asc') list.sort((a, b) => getEffectivePrice(a) - getEffectivePrice(b));
    if (sortOrder === 'stock-desc') list.sort((a, b) => (b.stock || 0) - (a.stock || 0));
    return list;
  }, [products, sortOrder, getEffectivePrice]);

  const printReceipt = (saleData) => {
    const printWindow = window.open('', '_blank', 'width=350,height=600');
    if (!printWindow) return;

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const saleDate = saleData.date ? new Date(saleData.date).toLocaleString('pt-BR') : new Date().toLocaleString('pt-BR');

    // Lógica para tipo de pagamento detalhado (Futuro API)
    let paymentDetail = saleData.paymentMethod;
    if (saleData.cardDetails) {
        // [GS-NEW] Exibe detalhes do cartão no recibo
        paymentDetail = `${saleData.cardDetails.type === 'credit' ? 'Crédito' : 'Débito'} (${saleData.cardDetails.installments || 1}x)`;
    } else if (saleData.payment_type_id) {
        if (saleData.payment_type_id === 'credit_card') paymentDetail = 'Crédito';
        if (saleData.payment_type_id === 'debit_card') paymentDetail = 'Débito';
    }

    const html = `
      <html>
      <head>
        <title>Comprovante GoldenSisters</title>
        <style>
          @page { margin: 0; }
          body { font-family: 'Courier New', monospace; font-size: 12px; margin: 0; padding: 5px; width: 80mm; color: #000; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .line { border-bottom: 1px dashed #000; margin: 5px 0; }
          .table { width: 100%; border-collapse: collapse; }
          .table td { vertical-align: top; padding: 2px 0; }
          .qty { width: 15%; }
          .desc { width: 55%; }
          .val { width: 30%; text-align: right; }
          .totals-box { margin-top: 10px; border-top: 1px dashed #000; padding-top: 5px; }
          .row-sb { display: flex; justify-content: space-between; }
          .footer { margin-top: 15px; font-size: 10px; text-align: center; line-height: 1.4; }
        </style>
      </head>
      <body>
        <div class="center bold" style="font-size: 14px;">GOLDENSISTERS SEMIJOIAS</div>
        <div class="center">CNPJ: 55.137.637/0001-16</div>
        <div class="line"></div>
        <div>Data: ${saleDate}</div>
        <div>Cliente: ${saleData.customerName || 'Consumidor Final'}</div>
        <div class="line"></div>
        <table class="table">
          <tr class="bold" style="font-size:10px;">
            <td class="qty">QTD</td>
            <td class="desc">ITEM</td>
            <td class="val">TOTAL</td>
          </tr>
          ${saleData.items.map(i => `
            <tr><td class="qty">${i.quantity}x</td><td class="desc">${i.name}</td><td class="val">${formatCurrency(i.price * i.quantity)}</td></tr>
          `).join('')}
        </table>
        <div class="totals-box">
          <div class="row-sb"><span>Subtotal:</span> <span>${formatCurrency(saleData.subtotal)}</span></div>
          ${saleData.discountPoints > 0 ? `<div class="row-sb"><span>Desc. Pontos:</span> <span>-${formatCurrency(saleData.discountPoints)}</span></div>` : ''}
          ${saleData.discountManualAmount > 0 ? `<div class="row-sb"><span>Desc. Manual:</span> <span>-${formatCurrency(saleData.discountManualAmount)}</span></div>` : ''}
          <div class="line"></div>
          <div class="row-sb bold" style="font-size:14px; margin-top:5px;"><span>TOTAL A PAGAR:</span> <span>${formatCurrency(saleData.total)}</span></div>
          
          ${saleData.amountPaid > 0 ? `
            <div class="row-sb" style="margin-top:5px;"><span>Valor Pago:</span> <span>${formatCurrency(saleData.amountPaid)}</span></div>
            <div class="row-sb"><span>Troco:</span> <span>${formatCurrency(saleData.change)}</span></div>
          ` : ''}

          <div class="center bold" style="margin-top:8px; font-size:12px;">Forma Pagto: ${paymentDetail}</div>
          
          <div class="line"></div>
          
          ${saleData.finalPointsBalance !== undefined ? `
            <div class="center bold" style="font-size:11px; margin-top:5px;">PROGRAMA DE FIDELIDADE</div>
            ${saleData.pointsEarned > 0 ? `<div class="row-sb" style="font-size:10px;"><span>Pontos Ganhos:</span> <span>+${saleData.pointsEarned} GP</span></div>` : ''}
            <div class="row-sb" style="font-size:10px;"><span>Saldo Atual:</span> <span>${saleData.finalPointsBalance} GP</span></div>
            <div class="line"></div>
          ` : ''}

        </div>
        <div class="footer">Rua Pedro de Toledo, 503<br>Vila Paulista - Cubatão - SP<br>CEP: 11510-090<br><br><span class="bold">Obrigado pela preferência!</span><br>Trocas somente com etiqueta afixada.</div>
        <script>window.print();setTimeout(() => window.close(), 2000);</script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleCancelSale = async (saleToCancel) => {
    if (!saleToCancel || !saleToCancel.id) return;
    if (saleToCancel.status === 'cancelled') {
        openModal(<p>Esta venda já está cancelada.</p>, "Aviso");
        return;
    }

    if (!window.confirm(`Deseja cancelar a venda de R$ ${saleToCancel.total?.toFixed(2)}? Os itens serão devolvidos ao estoque.`)) return;
    
    setIsProcessing(true);
    try {
        const batch = writeBatch(db);
        const saleRef = doc(db, "sales", saleToCancel.id);
        
        batch.update(saleRef, {
            status: "cancelled",
            cancelledAt: serverTimestamp(),
            cancelledBy: { uid: loggedInUser.uid, name: loggedInUser.name }
        });

        if (saleToCancel.items && Array.isArray(saleToCancel.items)) {
            saleToCancel.items.forEach(item => {
                if (item.productId) {
                    const productRef = doc(db, "products", item.productId);
                    batch.update(productRef, { stock: increment(item.quantity) });
                }
            });
        }

        // TODO: Futuramente, estornar também do financial_ledger (criar entrada de estorno/cancelamento)

        await batch.commit();
        openModal(<p>Venda cancelada e estoque estornado com sucesso!</p>, "Sucesso");
        fetchEssentialData();
    } catch (e) { 
        console.error("Erro no cancelamento batch:", e);
        openModal(<p>Erro ao cancelar: {e.message}</p>, "Erro"); 
    } finally { 
        setIsProcessing(false); 
    }
  };

  const RecentSalesList = ({ sessionId, onCancel }) => {
    const [sales, setSales] = useState([]);
    
    useEffect(() => {
        if (!sessionId) return;
        const q = query(collection(db, "sales"), where("sessionId", "==", sessionId), orderBy("createdAt", "desc"), limit(10));
        getDocs(q).then(snap => {
            setSales(snap.docs.map(d => ({id: d.id, ...d.data()})));
        }).catch(err => {
            console.error("Erro ao buscar histórico (Provável falta de índice):", err);
        });
    }, [sessionId]);

    return (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {sales.length === 0 && <p className="text-gray-400 text-center">Nenhuma venda encontrada ou índice ausente.</p>}
            {sales.map(s => (
                <div key={s.id} className="flex justify-between items-center bg-gray-800 p-3 rounded border border-gray-700">
                    <div className="flex flex-col">
                        <span className="font-bold text-white">R$ {s.total?.toFixed(2)}</span>
                        <span className="text-xs text-gray-400">{s.paymentMethod} • {s.createdAt?.seconds ? new Date(s.createdAt.seconds * 1000).toLocaleTimeString() : '...'}</span>
                    </div>
                    {s.status === 'cancelled' ? (
                        <span className="text-red-500 font-bold text-xs border border-red-500 px-2 py-1 rounded bg-red-900/20">CANCELADO</span>
                    ) : (
                        <button 
                            onClick={() => onCancel(s)} 
                            className="text-red-400 border border-red-400 px-3 py-1 rounded hover:bg-red-500 hover:text-white transition-colors text-xs font-bold uppercase"
                        >
                            Estornar
                        </button> 
                    )}
                </div>
            ))}
        </div>
    );
  };

  const handleOpenSession = async (initialAmount) => {
    const amount = parseFloat(initialAmount);
    if (isNaN(amount) || amount < 0) { openModal(<p>Por favor, insira um valor de abertura válido.</p>, "Valor Inválido"); return; }
    if (!loggedInUser) { openModal(<p>Você precisa estar logado para abrir o caixa.</p>, "Erro de Autenticação"); return; }
    setIsProcessing(true);
    try {
      const newSession = { openedAt: serverTimestamp(), openedBy: { uid: loggedInUser.uid, name: loggedInUser.name }, initialAmount: amount, status: "open", salesCount: 0, totalSalesValue: 0, cashMovements: [{ type: 'initial_supply', amount, description: 'Suprimento inicial de caixa', timestamp: new Date(), registeredBy: { uid: loggedInUser.uid, name: loggedInUser.name } }], paymentSummaries: {} };
      const docRef = await addDoc(collection(db, "cashier_sessions"), newSession);
      setCashierSession({ id: docRef.id, ...newSession });
      await fetchEssentialData();
      closeModal();
    } catch (error) { console.error("Erro ao abrir o caixa:", error); openModal(<p>Não foi possível iniciar uma nova sessão de caixa: {error.message}</p>, "Erro"); } finally { setIsProcessing(false); }
  };

  const handleCloseSession = async () => {
    if (!cashierSession || !loggedInUser) return;
    const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
    const netCashMovements = (cashierSession.cashMovements || []).reduce((s, m) => s + (m.amount || 0), 0);
    let cashSalesTotal = 0;
    if(cashierSession.paymentSummaries) {
        if(cashierSession.paymentSummaries["Dinheiro"]) cashSalesTotal += cashierSession.paymentSummaries["Dinheiro"];
        if(cashierSession.paymentSummaries["Pix"]) cashSalesTotal += cashierSession.paymentSummaries["Pix"];
    }
    const calculatedCashBalance = netCashMovements + cashSalesTotal;
    const confirmCloseModalContent = (
      <div>
        <p className="mb-4 text-gray-300 text-lg">Valor em caixa para fechamento: <span className="font-bold text-brand-gold">{formatCurrency(calculatedCashBalance)}</span></p>
        <p className="mb-6 text-gray-400">Confirma este valor e deseja fechar o caixa?</p>
        <div className="flex justify-end gap-4">
          <button onClick={() => closeModal()} className="px-6 py-3 bg-gray-600 text-white font-bold rounded-lg transition-all hover:bg-gray-500">Cancelar</button>
          <button onClick={async () => {
              setIsProcessing(true);
              try {
                const sessionRef = doc(db, "cashier_sessions", cashierSession.id);
                await updateDoc(sessionRef, { status: "closed", closedAt: serverTimestamp(), closedBy: { uid: loggedInUser.uid, name: loggedInUser.name } });
                setCashierSession(null); setCart([]); setSelectedCustomer(null);
                openModal(<p>Caixa fechado com sucesso!</p>, "Caixa Fechado");
              } catch (error) { console.error("Erro ao fechar o caixa: ", error); openModal(<p>Ocorreu um erro ao fechar o caixa: {error.message}</p>, "Erro"); } finally { setIsProcessing(false); closeModal(); }
            }} disabled={isProcessing} className="px-6 py-3 bg-green-600 text-white font-bold rounded-lg transition-all hover:bg-green-500 disabled:bg-gray-600">
            {isProcessing ? <Loader2 className="animate-spin mr-2" /> : ''} Confirmar Fechamento
          </button>
        </div>
      </div>
    );
    openModal(confirmCloseModalContent, "Confirmar Fechamento de Caixa");
  };

  const openCashMovementModal = (type) => {
    const handleSaveMovement = async (movementType, initialAmount, description) => {
        const amount = parseFloat(initialAmount);
        const trimmedDescription = description.trim();
        if (isNaN(amount) || amount <= 0) { openModal(<p>Por favor, insira um valor válido para a movimentação.</p>, "Valor Inválido"); return; }
        if (!trimmedDescription) { openModal(<p>Por favor, insira uma descrição para a movimentação.</p>, "Descrição Necessária"); return; }
        if (!cashierSession || !loggedInUser) { openModal(<p>A sessão do caixa não está ativa.</p>, "Erro de Sessão"); return; }
        setIsProcessing(true);
        try {
            const sessionRef = doc(db, "cashier_sessions", cashierSession.id);
            const newMovement = { type: movementType, amount: (movementType === 'withdrawal' || movementType === 'expense') ? -amount : amount, description: trimmedDescription, timestamp: new Date(), registeredBy: { uid: loggedInUser.uid, name: loggedInUser.name } };
            await updateDoc(sessionRef, { cashMovements: [...(cashierSession.cashMovements || []), newMovement], });
            setCashierSession(prev => ({ ...prev, cashMovements: [...(prev.cashMovements || []), newMovement], }));
            openModal(<p>Movimentação de {movementType === 'supply' ? 'suprimento' : movementType === 'withdrawal' ? 'sangria' : 'despesa'} registrada com sucesso!</p>, "Sucesso");
            closeModal();
        } catch (error) { console.error("Erro ao registrar movimentação de caixa:", error); openModal(<p>Não foi possível registrar a movimentação: {error.message}</p>, "Erro"); } finally { setIsProcessing(false); }
    };
    openModal(<CashMovementModalContent type={type} onSave={handleSaveMovement} isProcessing={isProcessing} />, type === 'supply' ? "Registrar Suprimento" : type === 'withdrawal' ? "Registrar Sangria" : "Registrar Despesa");
  };

  const handleSelectCustomer = (customer) => { setSelectedCustomer(customer); setDiscountInfo({ discount: 0, cost: 0 }); };
  const handleSelectWalkIn = () => { setSelectedCustomer(WALK_IN_CUSTOMER); setDiscountInfo({ discount: 0, cost: 0 }); };

  const handleAddToCart = (product) => {
    setCart(currentCart => {
      const existingItem = currentCart.find(item => item.id === product.id);
      if (existingItem) {
        const newQuantity = existingItem.quantity + 1;
        if (newQuantity > product.stock) { openModal(<p>Estoque insuficiente para o produto: {product.name}</p>, "Estoque Esgotado"); return currentCart; }
        return currentCart.map(item => item.id === product.id ? { ...item, quantity: newQuantity } : item);
      }
      if (1 > product.stock) { openModal(<p>Estoque insuficiente para o produto: {product.name}</p>, "Estoque Esgotado"); return currentCart; }
      
      const effectivePrice = getEffectivePrice(product);
      return [...currentCart, { ...product, price: effectivePrice, quantity: 1 }];
    });
  };

  const handleCartUpdate = (newCart) => setCart(newCart);
  const handleDiscountUpdate = useCallback((data) => setDiscountInfo({ discount: data.discount || 0, cost: data.cost || 0 }), []);
  const handleManualDiscountPercentageChange = useCallback((percentage) => setManualDiscountPercentage(percentage), []);
  const resetSale = useCallback(() => { setCart([]); setSelectedCustomer(null); setDiscountInfo({ discount: 0, cost: 0 }); setManualDiscountPercentage(0); fetchEssentialData(); }, [fetchEssentialData]);

  // [GS-MOD] Transação Principal - Atualizada para aceitar cardDetails
  const executeSaleTransaction = async (paymentMethod, amountPaid = 0, change = 0, cardDetails = null) => {
    const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    const pointsDiscount = Math.max(discountInfo.discount || 0, 0);
    const baseForManualDiscount = Math.max(subtotal - pointsDiscount, 0);
    const unclampedManualAmount = baseForManualDiscount * (Math.max(manualDiscountPercentage, 0) / 100);
    const manualDiscountAmount = Math.min(unclampedManualAmount, baseForManualDiscount);
    const finalTotal = Math.max(baseForManualDiscount - manualDiscountAmount, 0);

    setIsProcessing(true);

    const saleRef = doc(collection(db, "sales"));

    try {
      await runTransaction(db, async (transaction) => {
        const sessionRef = doc(db, "cashier_sessions", cashierSession.id);
        const sessionDoc = await transaction.get(sessionRef);
        if (!sessionDoc.exists() || sessionDoc.data().status !== 'open') { throw new Error("A sessão do caixa foi fechada ou não existe."); }

        const productRefs = cart.map(item => doc(db, "products", item.id));
        const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));

        let userDoc = null;
        if (selectedCustomer.id !== WALK_IN_CUSTOMER.id) {
          const userRef = doc(db, "users", selectedCustomer.id);
          userDoc = await transaction.get(userRef);
          if (!userDoc.exists()) { throw new Error(`Cliente ${selectedCustomer.name} não encontrado.`); }
        }

        for (let i = 0; i < cart.length; i++) {
          const item = cart[i];
          const productDoc = productDocs[i];
          if (!productDoc.exists() || productDoc.data().stock < item.quantity) { throw new Error(`Estoque insuficiente para: ${item.name}`); }
        }

        if (discountInfo.cost > 0 && selectedCustomer.id !== WALK_IN_CUSTOMER.id) {
          if (!userDoc || userDoc.data().availablePoints < discountInfo.cost) { throw new Error(`Pontos insuficientes para o cliente: ${selectedCustomer.name}`); }
        }
        
        let actualPaymentMethod = paymentMethod;
        if (finalTotal <= 0 && discountInfo.cost > 0) actualPaymentMethod = "Pago com Pontos";
        if (finalTotal === 0 && discountInfo.cost === 0 && !actualPaymentMethod) actualPaymentMethod = "Cortesia/100% Desconto";

        let pointsEarned = 0;
        let finalPointsBalance = selectedCustomer?.availablePoints || 0;

        if (selectedCustomer.id !== WALK_IN_CUSTOMER.id && userDoc) {
          const initialPoints = userDoc.data().availablePoints || 0;
          pointsEarned = Math.floor(finalTotal / 25);
          const pointsChange = (pointsEarned > 0 ? pointsEarned : 0) - (discountInfo.cost || 0);
          finalPointsBalance = initialPoints + pointsChange;
        }

        const saleData = { 
            sessionId: cashierSession.id, 
            customerId: selectedCustomer.id, 
            customerName: selectedCustomer.name, 
            items: cart.map(item => ({ productId: item.id, name: item.name, quantity: item.quantity, price: item.price })), 
            subtotal, 
            discountPoints: pointsDiscount, 
            pointsUsed: discountInfo.cost || 0, 
            discountManualPercentage: manualDiscountPercentage, 
            discountManualAmount: manualDiscountAmount, 
            total: finalTotal, 
            paymentMethod: actualPaymentMethod, 
            date: new Date(), 
            createdAt: serverTimestamp(), 
            createdBy: { uid: loggedInUser.uid, name: loggedInUser.name }, 
            pointsEarned, 
            finalPointsBalance, 
            status: "completed",
            amountPaid: amountPaid,
            change: change,
            // [GS-NEW] Salva detalhes do cartão (tipo e parcelas) se existirem
            ...(cardDetails && { cardDetails: cardDetails }) 
        };
        transaction.set(saleRef, saleData);

        for (const item of cart) { transaction.update(doc(db, "products", item.id), { stock: increment(-item.quantity) }); }

        if (selectedCustomer.id !== WALK_IN_CUSTOMER.id && userDoc) {
          const userRef = doc(db, "users", selectedCustomer.id);
          const pointsChange = (pointsEarned > 0 ? pointsEarned : 0) - (discountInfo.cost || 0);
          if (pointsChange !== 0) transaction.update(userRef, { availablePoints: increment(pointsChange) });
          if ((discountInfo.cost || 0) > 0) { transaction.set(doc(collection(userRef, 'point_transactions')), { type: 'redeem', amount: -(discountInfo.cost || 0), saleId: saleRef.id, date: serverTimestamp(), description: `Resgate de ${(discountInfo.cost || 0)} pontos na venda` }); }
          if (pointsEarned > 0) { transaction.set(doc(collection(userRef, 'point_transactions')), { type: 'earn', amount: pointsEarned, saleId: saleRef.id, date: serverTimestamp(), description: `Ganho de ${pointsEarned} pontos na venda` }); }
        }

        const currentPaymentSummaries = sessionDoc.data().paymentSummaries || {};
        const updatedPaymentSummaries = { ...currentPaymentSummaries };

        if (actualPaymentMethod === "Pago com Pontos") { updatedPaymentSummaries["Pago com Pontos"] = (updatedPaymentSummaries["Pago com Pontos"] || 0) + (pointsDiscount > 0 ? pointsDiscount : 0); } 
        else if (actualPaymentMethod && finalTotal > 0) { updatedPaymentSummaries[actualPaymentMethod] = (updatedPaymentSummaries[actualPaymentMethod] || 0) + finalTotal; }

        transaction.update(sessionRef, { salesCount: increment(1), totalSalesValue: increment(finalTotal), paymentSummaries: updatedPaymentSummaries });
      });

      // [GS-NEW] Integração Financeira Pós-Transação (Assíncrona)
      try {
        await financialService.addEntry({
            type: FINANCIAL_TYPES.INCOME,
            amountGross: finalTotal,
            amountNet: finalTotal, // No futuro, abater taxas de cartão aqui
            description: `Venda PDV - ${selectedCustomer.name}`,
            category: { id: 'sales', name: 'Vendas', path: 'Receitas > Vendas' },
            status: FINANCIAL_STATUS.PAID,
            paymentBreakdown: {
                method: paymentMethod || 'Dinheiro',
                amountPaid: amountPaid,
                change: change,
                // [GS-NEW] Adicionando metadados financeiros se for cartão
                ...(cardDetails && { cardType: cardDetails.type, installments: cardDetails.installments })
            },
            dates: {
                issue: new Date(),
                payment: new Date()
            },
            origin: {
                module: 'POS',
                referenceId: saleRef.id
            }
        });
        console.log("Venda registrada no Módulo Financeiro com sucesso.");
      } catch (finError) {
        console.error("ERRO CRÍTICO: Falha ao registrar venda no financeiro:", finError);
      }

      // [GS-UI] Modal de Sucesso
      const saleData = {
        customerName: selectedCustomer.name,
        items: cart.map(item => ({ name: item.name, quantity: item.quantity, price: item.price })),
        subtotal,
        discountPoints: pointsDiscount,
        discountManualAmount: manualDiscountAmount,
        total: finalTotal,
        amountPaid,
        change,
        paymentMethod,
        // [GS-NEW] Adiciona cardDetails para a função de impressão se necessário
        cardDetails: cardDetails, 
        pointsEarned: selectedCustomer.id !== WALK_IN_CUSTOMER.id ? Math.floor(finalTotal / 25) : 0,
        finalPointsBalance: selectedCustomer.availablePoints
      };

      const successModalContent = ( 
          <div className="bg-white rounded-lg p-2 text-brand-black w-full"> 
              <div className="flex flex-col items-center mb-4">
                  <CheckCircle className="text-green-600 mb-2" size={48} />
                  <h2 className="text-xl font-bold text-gray-800">Venda Finalizada!</h2>
              </div>
              
              {change > 0 && (
                  <div className="bg-gray-100 p-4 rounded-lg mb-4 text-center border border-gray-300">
                      <p className="text-sm text-gray-600 uppercase font-bold">Troco a devolver</p>
                      <p className="text-3xl font-bold text-green-600">R$ {change.toFixed(2)}</p>
                  </div>
              )}
              
              <div className="flex flex-col gap-3 mt-4"> 
                  <button onClick={() => { printReceipt(saleData); }} className="w-full flex items-center justify-center p-3 bg-brand-black text-white font-bold rounded-lg transition-all hover:bg-gray-800 shadow-lg"> 
                      <Printer className="mr-2" size={20} /> Imprimir Comprovante
                  </button>
                  <button onClick={() => { closeModal(); resetSale(); }} className="w-full flex items-center justify-center p-3 bg-green-600 text-white font-bold rounded-lg transition-all hover:bg-green-500 shadow-lg"> 
                      Nova Venda 
                  </button> 
              </div> 
          </div> 
      );
      openModal(successModalContent, "Sucesso");

    } catch (error) { 
      console.error("Erro ao finalizar a venda: ", error); 
      openModal( <div> <p className="text-red-400">Ocorreu um erro ao finalizar a venda.</p> <p className="text-xs text-gray-400 mt-2">Detalhes: {error.message}</p> </div>, "Erro na Transação" ); 
    } finally { setIsProcessing(false); }
  };

  const handleFinalizeSale = (paymentMethod) => {
    const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    const pointsDiscount = Math.max(discountInfo.discount || 0, 0);
    const baseForManualDiscount = Math.max(subtotal - pointsDiscount, 0);
    const unclampedManualAmount = baseForManualDiscount * (Math.max(manualDiscountPercentage, 0) / 100);
    const manualDiscountAmount = Math.min(unclampedManualAmount, baseForManualDiscount);
    const finalTotal = Math.max(baseForManualDiscount - manualDiscountAmount, 0);

    if (!selectedCustomer || cart.length === 0 || (finalTotal > 0 && !paymentMethod) || !loggedInUser || !cashierSession || cashierSession.status !== 'open') { 
        openModal(<p>Verifique os dados da venda (cliente, carrinho, pagamento, sessão).</p>, "Atenção"); 
        return; 
    }

    // 1. Fluxo: Dinheiro (Troco)
    if (paymentMethod === 'Dinheiro' && finalTotal > 0) {
        const CashPaymentModal = () => {
            const [amountPaidInput, setAmountPaidInput] = useState('');
            const [change, setChange] = useState(0);
            
            useEffect(() => {
                const paid = parseFloat(amountPaidInput) || 0;
                setChange(Math.max(paid - finalTotal, 0));
            }, [amountPaidInput]);

            return (
                <div className="flex flex-col gap-4 bg-white p-2 rounded-lg text-brand-black w-full">
                    <div className="bg-gray-100 p-4 rounded border border-gray-200">
                        <div className="flex justify-between text-lg mb-2">
                            <span className="font-semibold text-gray-600">Total a Pagar:</span>
                            <span className="font-bold text-gray-900 text-xl">R$ {finalTotal.toFixed(2)}</span>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Valor Recebido (R$)</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                            <input 
                                type="number" 
                                autoFocus
                                className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 text-2xl font-bold focus:ring-2 focus:ring-brand-gold focus:border-transparent outline-none shadow-inner"
                                placeholder="0.00"
                                value={amountPaidInput}
                                onChange={(e) => setAmountPaidInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const paid = parseFloat(amountPaidInput);
                                        if (paid >= finalTotal) {
                                            executeSaleTransaction(paymentMethod, paid, paid - finalTotal);
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className={`p-4 rounded border text-center transition-colors ${
                        (parseFloat(amountPaidInput) || 0) >= finalTotal ? 'bg-green-100 border-green-300' : 'bg-red-50 border-red-200'
                    }`}>
                        <p className="text-sm font-bold text-gray-500 mb-1">Troco Estimado</p>
                        <p className={`text-3xl font-bold ${(parseFloat(amountPaidInput) || 0) >= finalTotal ? 'text-green-600' : 'text-gray-400'}`}>
                            R$ {change.toFixed(2)}
                        </p>
                    </div>
                    <button 
                        onClick={() => {
                            const paid = parseFloat(amountPaidInput);
                            if (paid >= finalTotal) {
                                executeSaleTransaction(paymentMethod, paid, change);
                            }
                        }}
                        disabled={!amountPaidInput || parseFloat(amountPaidInput) < finalTotal || isProcessing}
                        className="w-full flex items-center justify-center p-4 bg-brand-black text-white font-bold rounded-lg transition-all hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed mt-2 shadow-lg"
                    >
                        {isProcessing ? <Loader2 className="animate-spin mr-2"/> : <Calculator className="mr-2" />}
                        Confirmar Pagamento
                    </button>
                </div>
            );
        };
        openModal(<CashPaymentModal />, "Pagamento em Dinheiro");
        return;
    }

    // 2. [GS-NEW] Fluxo: Cartão (Modal de Detalhes -> Modal de Integração)
    if (paymentMethod === 'Cartão' && finalTotal > 0) {
        
        // Componente interno para detalhes do cartão (Débito vs Crédito)
        const CardDetailsModal = ({ onConfirm }) => {
            const [type, setType] = useState('credit');
            const [installments, setInstallments] = useState(1);
            
            // [GS-FIX] Variável removida para evitar warning

            return (
                <div className="flex flex-col gap-6 bg-white p-4 rounded-lg text-brand-black w-full">
                    <div className="bg-gray-50 p-4 rounded border border-gray-200 text-center">
                        <p className="text-sm text-gray-500 uppercase font-bold">Total da Venda</p>
                        <p className="text-3xl font-bold text-gray-900">R$ {finalTotal.toFixed(2)}</p>
                    </div>

                    {/* Seleção de Tipo */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Função do Cartão</label>
                        <div className="grid grid-cols-2 gap-4">
                            <button 
                                onClick={() => setType('debit')}
                                className={`p-4 rounded-lg border-2 font-bold transition-all ${
                                    type === 'debit' 
                                    ? 'border-brand-gold bg-brand-gold/10 text-brand-dark' 
                                    : 'border-gray-200 text-gray-400 hover:border-gray-300'
                                }`}
                            >
                                DÉBITO
                            </button>
                            <button 
                                onClick={() => setType('credit')}
                                className={`p-4 rounded-lg border-2 font-bold transition-all ${
                                    type === 'credit' 
                                    ? 'border-brand-gold bg-brand-gold/10 text-brand-dark' 
                                    : 'border-gray-200 text-gray-400 hover:border-gray-300'
                                }`}
                            >
                                CRÉDITO
                            </button>
                        </div>
                    </div>

                    {/* Seleção de Parcelas (Apenas Crédito) */}
                    {type === 'credit' && (
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Parcelamento</label>
                            <select 
                                value={installments}
                                onChange={(e) => setInstallments(Number(e.target.value))}
                                className="w-full p-3 bg-white border border-gray-300 rounded-lg text-lg font-medium focus:ring-2 focus:ring-brand-gold outline-none"
                            >
                                {[...Array(12)].map((_, i) => {
                                    const num = i + 1;
                                    const val = finalTotal / num;
                                    return (
                                        <option key={num} value={num}>
                                            {num}x de R$ {val.toFixed(2)} {num === 1 ? '(À vista)' : ''}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    <button 
                        onClick={() => onConfirm({ type, installments: type === 'debit' ? 1 : installments })}
                        className="w-full py-4 bg-brand-black text-white font-bold rounded-lg hover:bg-gray-800 shadow-lg mt-2 flex items-center justify-center"
                    >
                        Continuar para Maquininha <CreditCard className="ml-2" size={20} />
                    </button>
                </div>
            );
        };

        // Modal de Integração (Reutilizado e adaptado)
        const PaymentIntegrationModal = ({ method, cardDetails }) => {
            const [step, setStep] = useState(0); 
            const [statusMessage, setStatusMessage] = useState('Iniciando conexão...');
            const [isSimulation, setIsSimulation] = useState(false);
            const deviceId = process.env.REACT_APP_MP_DEVICE_ID;

            useEffect(() => {
                const startProcess = async () => {
                    setStep(1);
                    setStatusMessage('Verificando terminal...');
                    if (!deviceId) {
                        setIsSimulation(true);
                        setTimeout(() => {
                            setStep(2);
                            setStatusMessage('Insira o cartão na maquininha...');
                        }, 1500);
                        return;
                    }
                    setStep(2);
                    setStatusMessage('Comando enviado. Aguarde pagamento...');
                };
                startProcess();
            }, [deviceId]);

            const handleConfirmPayment = () => {
                setStep(3); 
                setStatusMessage('Pagamento confirmado!');
                setTimeout(() => {
                    // [GS-NEW] Passa os cardDetails para a transação
                    executeSaleTransaction(method, finalTotal, 0, cardDetails); 
                }, 1000);
            };

            return (
                <div className="flex flex-col items-center justify-center text-center space-y-4 py-2 bg-white rounded-lg text-brand-black w-full">
                    <div className="relative p-4">
                        {step === 1 && <Wifi className="animate-pulse text-blue-500" size={64} />}
                        {step === 2 && <CreditCard className="animate-pulse text-brand-gold-muted" size={64} />}
                        {step === 3 && <CheckCircle className="text-green-600" size={64} />}
                        {step === 4 && <AlertTriangle className="text-red-500" size={64} />}
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {step === 2 ? 'Aguardando Cartão' : 'Processando'}
                        </h3>
                        <p className="text-gray-500 animate-pulse font-medium">{statusMessage}</p>
                        {cardDetails && (
                            <p className="text-xs text-gray-400 mt-2 font-mono">
                                {cardDetails.type === 'credit' ? 'CRÉDITO' : 'DÉBITO'} • {cardDetails.installments}x
                            </p>
                        )}
                    </div>
                    {isSimulation && step < 3 && (
                        <div className="bg-yellow-50 border border-yellow-200 p-2 rounded text-xs text-yellow-800 font-semibold">
                            <p>Modo Simulação Ativo</p>
                        </div>
                    )}
                    {step === 2 && (
                        <button onClick={handleConfirmPayment} className="mt-4 px-8 py-3 bg-brand-black hover:bg-gray-800 text-white font-bold rounded-lg shadow-lg flex items-center gap-2">
                            <CheckCircle size={20} /> Confirmar Pagamento
                        </button>
                    )}
                </div>
            );
        };

        // Abre o primeiro Modal (Detalhes)
        openModal(
            <CardDetailsModal 
                onConfirm={(details) => {
                    // Ao confirmar detalhes, abre o segundo Modal (Integração) passando os dados
                    openModal(<PaymentIntegrationModal method={paymentMethod} cardDetails={details} />, "Integração Maquininha");
                }} 
            />, 
            "Detalhes do Cartão"
        );
        return;
    }

    // 3. Fluxo: Pix (Mantido direto)
    if (paymentMethod === 'Pix' && finalTotal > 0) {
        const PixIntegrationModal = () => {
             // ... (Lógica simplificada do Pix, similar ao anterior mas sem steps de crédito)
             // Para manter consistência com o código original, usando a estrutura genérica aqui
             // mas focada em Pix
             const [step, setStep] = useState(2); // Vai direto para aguardando
             const [isSimulation] = useState(!process.env.REACT_APP_MP_DEVICE_ID);
             
             const handleConfirm = () => {
                 setStep(3);
                 setTimeout(() => executeSaleTransaction('Pix', finalTotal, 0), 1000);
             };

             return (
                 <div className="flex flex-col items-center justify-center text-center space-y-4 py-2 bg-white rounded-lg text-brand-black w-full">
                     <div className="relative p-4">
                        {step === 2 && <Smartphone className="animate-bounce text-brand-gold-muted" size={64} />}
                        {step === 3 && <CheckCircle className="text-green-600" size={64} />}
                     </div>
                     <h3 className="text-xl font-bold text-gray-800">Aguardando Pix</h3>
                     <p className="text-gray-500">QR Code gerado na tela da maquininha...</p>
                     {isSimulation && <p className="text-xs text-yellow-600 font-bold">Modo Simulação</p>}
                     <button onClick={handleConfirm} className="mt-4 px-8 py-3 bg-brand-black text-white font-bold rounded-lg shadow-lg">Confirmar Pagamento</button>
                 </div>
             );
        };
        openModal(<PixIntegrationModal />, "Pagamento Pix");
        return;
    }

    // Fluxo Padrão (Outros/Cortesia)
    executeSaleTransaction(paymentMethod, 0, 0);
  };

  if (isSessionLoading || !loggedInUser) { return ( <div className="flex h-full w-full items-center justify-center bg-brand-black text-white"> <Loader2 className="h-12 w-12 animate-spin text-brand-gold" /> <span className="ml-4 text-xl">Carregando sessão...</span> </div> ); }

  if (!cashierSession) {
    return (
      <div className="flex flex-col h-full w-full items-center justify-center bg-gray-900 text-white">
        <div className="text-center bg-brand-dark p-10 rounded-lg shadow-lg">
          <Lock size={64} className="mx-auto text-brand-gold mb-4" />
          <h1 className="text-3xl font-bold mb-2">Caixa Fechado</h1>
          <p className="text-gray-400 mb-6">Você precisa abrir o caixa para iniciar as vendas.</p>
          <button onClick={() => { const ModalContent = ( <div> <p className="mb-4 text-gray-300">Informe o valor inicial (suprimento) para abrir o caixa.</p> <div className="relative"> <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span> <input ref={amountInputRef} type="number" placeholder="0.00" autoFocus className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md focus:ring-brand-gold focus:border-brand-gold text-white" onKeyDown={(e) => { if (e.key === 'Enter' && amountInputRef.current) handleOpenSession(amountInputRef.current.value); }} /> </div> <button onClick={() => { if (amountInputRef.current) handleOpenSession(amountInputRef.current.value); }} disabled={isProcessing} className="w-full flex items-center justify-center p-3 mt-6 bg-green-600 text-white font-bold rounded-lg transition-all hover:bg-green-500 disabled:bg-gray-600" > {isProcessing ? <Loader2 className="animate-spin mr-2" /> : <Unlock className="mr-2" />} Confirmar e Abrir Caixa </button> </div> ); openModal(ModalContent, "Abrir Caixa"); }} disabled={isProcessing} className="w-full flex items-center justify-center p-4 bg-green-600 text-white font-bold rounded-lg transition-all hover:bg-green-500 disabled:bg-gray-600" > Abrir Caixa </button>
        </div>
      </div>
    );
  }

  if (isLoading) { return ( <div className="flex h-full w-full items-center justify-center bg-brand-black text-white"> <Loader2 className="h-12 w-12 animate-spin text-brand-gold" /> <span className="ml-4 text-xl">Carregando produtos...</span> </div> ); }

  const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const pointsDiscount = Math.max(discountInfo.discount || 0, 0);
  const baseForManualDiscount = Math.max(subtotal - pointsDiscount, 0);
  const unclampedManualAmount = baseForManualDiscount * (Math.max(manualDiscountPercentage, 0) / 100);
  const manualDiscountAmount = Math.min(unclampedManualAmount, baseForManualDiscount);
  const finalTotal = Math.max(baseForManualDiscount - manualDiscountAmount, 0);

  return (
    <div className="grid h-[calc(100vh-theme(space.16))] grid-cols-1 lg:grid-cols-3 gap-4 p-4 bg-gray-900 text-white">
      <div className="lg:col-span-2 flex flex-col gap-4 overflow-hidden">
        <div className="flex items-center justify-between px-1 text-gray-400 text-sm">
           <div className="flex items-center"><Package size={14} className="mr-1.5"/><span>{products.length} produtos disponíveis</span></div>
           <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="bg-gray-800 border border-gray-600 rounded-md text-sm px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm cursor-pointer hover:bg-gray-700 transition-colors">
             <option value="default">Padrão</option>
             <option value="price-desc">Maior Preço</option>
             <option value="price-asc">Menor Preço</option>
             <option value="stock-desc">Maior Estoque</option>
           </select>
        </div>
        <ProductGrid products={sortedProducts} onAddToCart={handleAddToCart} />
      </div>

      <div className="lg:col-span-1 flex flex-col bg-brand-dark rounded-lg p-4 gap-4">
        <div className="flex-shrink-0 flex items-center justify-between gap-4 mb-4">
          <CustomerSearch customers={allCustomers} onSelectCustomer={handleSelectCustomer} selectedCustomer={selectedCustomer} onSelectWalkIn={handleSelectWalkIn} />
          
          <div className="flex gap-2">
            <button 
                onClick={() => openModal(<RecentSalesList sessionId={cashierSession.id} onCancel={handleCancelSale} />, "Histórico de Vendas")} 
                title="Histórico / Cancelar" 
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:bg-gray-600 transition-colors"
            >
                <History size={20} />
            </button>
            <button onClick={handleCloseSession} disabled={isProcessing} title="Fechar o Caixa" className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-500 disabled:bg-gray-600 transition-colors">
                {isProcessing ? <Loader2 size={20} className="animate-spin" /> : <Lock size={20} />}
            </button>
          </div>
        </div>

        <div className="flex flex-shrink-0 gap-2 mb-4">
            <button onClick={() => openCashMovementModal('supply')} disabled={isProcessing} className="flex-1 flex items-center justify-center p-2 bg-blue-600 text-white font-bold rounded-md transition-all hover:bg-blue-500 disabled:bg-gray-600 text-sm" title="Registrar Suprimento">
                <PlusCircle size={16} className="mr-1" /> Suprimento
            </button>
            <button onClick={() => openCashMovementModal('withdrawal')} disabled={isProcessing} className="flex-1 flex items-center justify-center p-2 bg-orange-600 text-white font-bold rounded-md transition-all hover:bg-orange-500 disabled:bg-gray-600 text-sm" title="Registrar Sangria">
                <MinusCircle size={16} className="mr-1" /> Sangria
            </button>
            <button onClick={() => openCashMovementModal('expense')} disabled={isProcessing} className="flex-1 flex items-center justify-center p-2 bg-purple-600 text-white font-bold rounded-md transition-all hover:bg-purple-500 disabled:bg-gray-600 text-sm" title="Registrar Despesa">
                <DollarSign size={16} className="mr-1" /> Despesa
            </button>
        </div>

        {selectedCustomer && selectedCustomer.id !== WALK_IN_CUSTOMER.id && (
          <div className="flex-shrink-0">
            <PointsCard customer={selectedCustomer} subtotal={subtotal} onDiscountUpdate={handleDiscountUpdate} planRules={planRules} />
          </div>
        )}

        <div className="flex flex-col flex-grow min-h-0">
          {cart.length > 0 ? (
            <>
              <div className="flex-grow overflow-y-auto pr-2">
                <CartPane cart={cart} onUpdateCart={handleCartUpdate} products={products} onManualDiscountPercentageChange={handleManualDiscountPercentageChange} manualDiscountPercentage={manualDiscountPercentage} subtotal={subtotal} />
              </div>
              <div className="flex-shrink-0 mt-auto pt-4 border-t border-gray-700">
                <CartPane.Totals subtotal={subtotal} pointsDiscount={discountInfo.discount} manualDiscountPercentage={manualDiscountPercentage} total={finalTotal} />
                <CartPane.Payment onFinalizeSale={handleFinalizeSale} isProcessing={isProcessing} total={finalTotal} isDisabled={!selectedCustomer} />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <ShoppingCart size={64} />
              <p className="mt-4 text-lg">Adicione produtos para começar a venda.</p>
              <p className="text-sm">Busque ou selecione 'Consumidor Final' para associar um cliente.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default POSPage;